import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom'
import { GameSettingsProvider } from './contexts/GameSettingsContext'
import { GameProvider } from './contexts/GameContext'
import { ErrorBoundary } from './components/ErrorBoundary'
import { GameModePage } from './pages/GameModePage'
import GamePage from './pages/GamePage'
import { Layout } from './components/Layout'
import { GameCompletionPage } from './pages/GameCompletionPage'
import { StatisticsPage } from './pages/StatisticsPage'
import { SetterDashboardPage } from './pages/SetterDashboardPage'
import { PortalHomePage } from './pages/PortalHomePage'
import { AnagramModePage } from './pages/AnagramModePage'
import AnagramGamePage from './pages/AnagramGamePage'
import { AnagramCompletionPage } from './pages/AnagramCompletionPage'
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage'
import { DeleteAccountPage } from './pages/DeleteAccountPage'
import { AccountPage } from './pages/AccountPage'
import { AuthProvider } from './contexts/AuthContext'
import { AccountProviders } from './contexts/AccountProviders'
import { useEffect } from 'react'
import {
  setDeviceProperties,
  trackFirstVisit,
  trackReturnVisit,
  trackPlayTimeDistribution,
} from './services/gameAnalytics'
import { trackPageView } from './services/hotjar'

// Initialize Firebase services
import './services/firebase'

// App version - update this when new versions are released
export const APP_VERSION = '1.0.0'

// Route listener component to track page views
function RouteTracker() {
  const location = useLocation()

  useEffect(() => {
    // Track page view in Hotjar whenever location changes
    trackPageView(location.pathname)
  }, [location])

  return null
}

// Old root-level Wordle paths (/play/:mode, /complete, /statistics) moved
// under /zword/* once the portal home took over '/'. Wordle is a live
// product with real users, so bookmarked/shared links to the old paths
// must keep working rather than 404ing or bouncing to the portal.
function RedirectPlayMode() {
  const { mode } = useParams<{ mode: string }>()
  return <Navigate to={`/zword/play/${mode}`} replace />
}

function RedirectToZword({ suffix }: { suffix: string }) {
  const location = useLocation()
  return <Navigate to={`/zword${suffix}`} replace state={location.state} />
}

function App() {
  // Set device properties for analytics when the app loads
  useEffect(() => {
    try {
      // Set device properties for analytics
      setDeviceProperties()

      // Track play time distribution
      trackPlayTimeDistribution(new Date().getHours())

      // Check if this is the user's first visit or a return visit
      const lastVisitTimestamp = localStorage.getItem('lastVisitTimestamp')

      if (!lastVisitTimestamp) {
        // First visit
        trackFirstVisit()
      } else {
        // Return visit - calculate days since last visit
        const daysSinceLastVisit = Math.floor(
          (Date.now() - parseInt(lastVisitTimestamp)) / (1000 * 60 * 60 * 24)
        )

        // Only track return if it's been at least 1 day or 4 hours since last visit
        if (
          daysSinceLastVisit >= 1 ||
          Date.now() - parseInt(lastVisitTimestamp) >= 4 * 60 * 60 * 1000
        ) {
          trackReturnVisit(daysSinceLastVisit)
        }
      }

      // Update last visit timestamp
      localStorage.setItem('lastVisitTimestamp', Date.now().toString())

      // Check for version changes
      const previousVersion = localStorage.getItem('appVersion')
      if (previousVersion && previousVersion !== APP_VERSION) {
        // Track version upgrade
        import('./services/gameAnalytics').then(({ trackVersionUpgrade }) => {
          trackVersionUpgrade(previousVersion, APP_VERSION)
          localStorage.setItem('appVersion', APP_VERSION)
        })
      } else if (!previousVersion) {
        localStorage.setItem('appVersion', APP_VERSION)
      }
    } catch (error) {
      console.error('Failed to set device properties or track visits:', error)
    }
  }, [])

  return (
    <ErrorBoundary>
      <AuthProvider>
        <AccountProviders>
        <GameSettingsProvider>
          <GameProvider>
            <BrowserRouter>
              <RouteTracker />
              <Routes>
                <Route path="/" element={<Layout />}>
                  <Route index element={<PortalHomePage />} />

                  <Route path="account" element={<AccountPage />} />

                  <Route path="zword" element={<GameModePage />} />
                  <Route path="zword/play/:mode" element={<GamePage />} />
                  <Route path="zword/complete" element={<GameCompletionPage />} />
                  <Route path="zword/statistics" element={<StatisticsPage />} />

                  <Route path="anagram" element={<AnagramModePage />} />
                  <Route path="anagram/play/:mode" element={<AnagramGamePage />} />
                  <Route path="anagram/complete" element={<AnagramCompletionPage />} />

                  {/* Old root-level Wordle paths - kept working via redirect */}
                  <Route path="play/:mode" element={<RedirectPlayMode />} />
                  <Route path="complete" element={<RedirectToZword suffix="/complete" />} />
                  <Route path="statistics" element={<RedirectToZword suffix="/statistics" />} />

                  <Route path="setter" element={<SetterDashboardPage />} />
                  <Route path="privacy" element={<PrivacyPolicyPage />} />
                  <Route path="delete-account" element={<DeleteAccountPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
              </Routes>
            </BrowserRouter>
          </GameProvider>
        </GameSettingsProvider>
        </AccountProviders>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
